import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, IconButton, Tabs, Tab, Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Grid, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LeaveSummaryTable from "./LeaveSummaryTable";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { getLeaveBalanceSummary, getLeaveByUserId, getLeaveHistoryForSummary } from "../../services/leaveManagement.service";
import { getEarnedLeaveHistoryForSummary } from "../../services/earnedLeaveRequest.service";
import { getUserById } from "../../services/user.service";
import moment from "moment";
import EmptyData_Image from "../../assets/images/CommonTableImg/EmptyData_Icon.svg";

const LeaveSummary = () => {
    const [summaryData, setSummaryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (userId) {
            getUserById(userId).then((res) => {
                setCurrentUser(res.data?.data);
            }).catch(err => console.error(err));
        }
    }, []);

    // History Dialog State
    const [openHistory, setOpenHistory] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [tabValue, setTabValue] = useState(0);

    // Earned Credit History State
    const [earnedCreditDialog, setEarnedCreditDialog] = useState(false);
    const [earnedCreditData, setEarnedCreditData] = useState([]);
    const [earnedCreditLoading, setEarnedCreditLoading] = useState(false);

    const [selectedMonth, setSelectedMonth] = useState(moment().format("MM"));
    const [selectedYear, setSelectedYear] = useState(moment().format("YYYY"));

    const months = [
        { value: "All", label: "All" },
        { value: "01", label: "January" }, { value: "02", label: "February" },
        { value: "03", label: "March" }, { value: "04", label: "April" },
        { value: "05", label: "May" }, { value: "06", label: "June" },
        { value: "07", label: "July" }, { value: "08", label: "August" },
        { value: "09", label: "September" }, { value: "10", label: "October" },
        { value: "11", label: "November" }, { value: "12", label: "December" }
    ];

    const generateYears = () => {
        const currentYear = moment().year();
        const years = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            years.push(i);
        }
        return years;
    };

    useEffect(() => {
        if (currentUser || !localStorage.getItem("userId")) {
            fetchSummary();
        }
    }, [selectedMonth, selectedYear, currentUser]);

    const fetchSummary = () => {
        setLoading(true);
        getLeaveBalanceSummary({ month: selectedMonth, year: selectedYear })
            .then((res) => {
                const rawData = res.data?.data || [];
                // Flatten data for table
                const formattedData = rawData.map(user => ({
                    ...user,
                    userName: `${user.firstName} ${user.lastName}`,
                    // Available
                    available_earned: user.availableLeave?.earnedLeave || 0,
                    available_planned: user.availableLeave?.plannedLeave || 0,
                    available_unplanned: user.availableLeave?.unplannedLeave || 0,
                    // Availed (Taken)
                    availed_planned: user.leaveTaken?.plannedLeave || 0,
                    availed_unplanned: user.leaveTaken?.unplannedLeave || 0,
                    availed_lop: user.leaveTaken?.lop || 0,
                    availed_earned: user.leaveTaken?.earnedLeave || 0,
                }));
                // Filter if not admin/manager
                if (currentUser && !currentUser.isAdmin && !currentUser.isManager && !currentUser.isOverAllAdmin) {
                    const myData = formattedData.filter(user => user._id === currentUser._id);
                    setSummaryData(myData);
                } else {
                    setSummaryData(formattedData);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    };

    const handleHistory = (row) => {
        setSelectedUser(row);
        setOpenHistory(true);
        setHistoryLoading(true);
        setTabValue(0); // Reset tab

        let startDate = null;
        let endDate = null;

        if (selectedMonth && selectedMonth !== "All" && selectedYear) {
            startDate = moment(`${selectedYear}-${selectedMonth}-01`).startOf('month').format("DD/MM/YYYY");
            endDate = moment(`${selectedYear}-${selectedMonth}-01`).endOf('month').format("DD/MM/YYYY");
        } else if (selectedYear) {
            startDate = moment(`${selectedYear}-01-01`).startOf('year').format("DD/MM/YYYY");
            endDate = moment(`${selectedYear}-12-31`).endOf('year').format("DD/MM/YYYY");
        }

        // Pass date range to API
        getLeaveHistoryForSummary(row._id, { startDate, endDate, status: "Approved" })
            .then((res) => {
                setHistoryData(res.data?.data || []);
                setHistoryLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setHistoryLoading(false);
            });
    };

    const getAvailedHeaderLabel = () => {
        if (selectedMonth !== "All") {
            const monthLabel = months.find(m => m.value === selectedMonth)?.label;
            return `Availed (${monthLabel} ${selectedYear})`;
        }
        return `Availed (${selectedYear})`;
    };

    const getAvailableHeaderLabel = () => {
        if (selectedMonth !== "All") {
            const monthLabel = months.find(m => m.value === selectedMonth)?.label;
            return `Available (Till Today)`;
        }
        return `Available (Till Today)`;
    };

    const groupedHeaders = [
        [
            { label: "User Name", rowSpan: 2 },
            { label: getAvailableHeaderLabel(), colSpan: 3, align: "center" },
            {
                label: (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <span>Availed</span>
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "1px solid #d5d7e8",
                                outline: "none",
                                fontSize: "12px",
                                cursor: "pointer",
                                backgroundColor: "#fafafa",
                                fontWeight: 500
                            }}
                        >
                            {months.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "1px solid #d5d7e8",
                                outline: "none",
                                fontSize: "12px",
                                cursor: "pointer",
                                backgroundColor: "#fafafa",
                                fontWeight: 500
                            }}
                        >
                            {generateYears().map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                    </Box>
                ),
                colSpan: 4,
                align: "center"
            },
            { label: "Action", rowSpan: 2, align: "center" }
        ],
        [
            // Available Sub-headers
            { label: "Earned Leave" },
            { label: "Planned Leave" },
            { label: "Unplanned Leave" },
            // Availed Sub-headers
            { label: "Planned Leave" },
            { label: "Unplanned Leave" },
            { label: "LOP" },
            { label: "Earned Leave" }
        ]
    ];

    const columns = [
        { id: "userName", label: "User Name" },
        { id: "available_earned", label: "Earned Leave" },
        { id: "available_planned", label: "Planned Leave" },
        { id: "available_unplanned", label: "Unplanned Leave" },
        { id: "availed_planned", label: "Planned Leave" },
        { id: "availed_unplanned", label: "Unplanned Leave" },
        { id: "availed_lop", label: "LOP" },
        { id: "availed_earned", label: "Earned Leave" },
    ];

    const historyColumns = [
        { id: "date", label: "Date" },
        { id: "leaveType", label: "Leave Type" },
        { id: "duration", label: "Duration" },
        { id: "reason", label: "Reason" },
        { id: "updatedByName", label: "Approved By" },
    ];

    const earnedCreditColumns = [
        { id: "date", label: "Date" },
        { id: "type", label: "Type" },
        { id: "duration", label: "Duration" },
        { id: "count", label: "Count" },
        { id: "reason", label: "Reason" },
        { id: "updatedByName", label: "Approved By" },
    ];

    const handleEarnedCreditClick = (row) => {
        setEarnedCreditDialog(true);
        setEarnedCreditLoading(true);
        setEarnedCreditData([]);

        // Fetch both credits (earned leave requests) and debits (approved earned leave applications)
        Promise.all([
            getEarnedLeaveHistoryForSummary(row._id, { status: "Approved" }),
            getLeaveByUserId(row._id, {})
        ])
            .then(([creditsRes, debitsRes]) => {
                const credits = creditsRes.data?.data || [];
                const allLeaves = debitsRes.data?.data || [];

                // Filter only earned leave debits that are approved
                const earnedLeaveDebits = allLeaves.filter(leave => {
                    const leaveTypeName = leave.leaveType?.name || leave.leaveType || '';
                    const isEarnedLeave = leaveTypeName.toLowerCase().includes('earned');
                    const isApproved = leave.status === "Approved";
                    return isEarnedLeave && isApproved;
                });

                // Carry Forward record (Credit)
                const carryForward = row.availableLeave?.carryForwardFromPreviousYear || 0;
                const carryForwardRecord = {
                    date: "1st Jan",
                    type: "Credit",
                    duration: "---",
                    count: carryForward,
                    reason: "Carry Forward from Previous Year",
                    status: "Approved",
                    isCredit: true,
                    updatedByName: "System"
                };

                // Format credits (earned leave requests)
                const formattedCredits = credits.map(req => ({
                    date: moment(req.date).format("DD/MM/YYYY"),
                    type: "Credit",
                    duration: req.isHalfDay ? "Half Day" : "Full Day",
                    count: req.isHalfDay ? 0.5 : 1,
                    reason: req.reason,
                    status: req.status,
                    isCredit: true,
                    updatedByName: req.updatedByName || "N/A"
                }));

                // Format debits (approved earned leave applications)
                const formattedDebits = earnedLeaveDebits.map(leave => {
                    const isHalfDay = leave.leaveDayType && leave.leaveDayType.toLowerCase().includes('half');
                    const count = isHalfDay ? -0.5 : -1;
                    return {
                        date: moment(leave.leaveDate).format("DD/MM/YYYY"),
                        type: "Debit",
                        duration: leave.leaveDayType,
                        count: count,
                        countDisplay: `<span style="color: #f44336; font-weight: 600;">${count}</span>`,
                        reason: leave.reason || "Earned Leave Applied",
                        status: leave.status,
                        isCredit: false,
                        updatedByName: leave.updatedByName || "N/A"
                    };
                });

                // Combine all records and sort by date (newest first)
                const allRecords = [...formattedCredits, ...formattedDebits, carryForwardRecord];
                const sorted = allRecords.sort((a, b) => {
                    if (a.date === "1st Jan") return 1; // Carry forward at the end
                    if (b.date === "1st Jan") return -1;
                    return moment(b.date, "DD/MM/YYYY").diff(moment(a.date, "DD/MM/YYYY"));
                });

                setEarnedCreditData(sorted);
                setEarnedCreditLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setEarnedCreditLoading(false);
            });
    };

    // Group history data by leave type
    const groupedHistory = {
        "Planned Leave": [],
        "UnPlanned Leave": [], // API returns "UnPlanned Leave" or "Unplanned Leave"? Check casing
        "Earned Leave": [],
        "LOP": []
    };

    historyData.forEach(item => {
        const typeName = item.leaveType?.name || item.leaveType;
        // Normalize casing if needed, but let's assume API consistency or map it
        // Check for common variations
        if (typeName === "Planned Leave") groupedHistory["Planned Leave"].push(item);
        else if (typeName === "UnPlanned Leave" || typeName === "Unplanned Leave") groupedHistory["UnPlanned Leave"].push(item);
        else if (typeName === "Earned Leave") groupedHistory["Earned Leave"].push(item);
        else if (typeName === "LOP" || typeName === "Lop") groupedHistory["LOP"].push(item);
    });

    const activeTabs = Object.keys(groupedHistory).filter(key => groupedHistory[key].length > 0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const formatHistoryForTable = (data) => data.map(item => ({
        ...item,
        date: moment(item.leaveDate).format("DD-MM-YYYY"),
        duration: item.leaveDayType,
        leaveType: item.leaveType?.name || item.leaveType,
        updatedByName: item.updatedByName || "N/A",
    }));

    return (
        <Box sx={{ padding: 2 }}>
            <LeaveSummaryTable
                columns={columns}
                groupedHeaders={groupedHeaders}
                data={summaryData}
                openLoader={loading}
                tableConfig={{ isAction: true, isHistory: true }}
                handleHistory={handleHistory}
                handleEarnedCreditClick={handleEarnedCreditClick}
            />

            <Dialog
                open={openHistory}
                onClose={() => setOpenHistory(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {selectedUser?.userName}'s Leave History
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenHistory(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <Typography>Loading...</Typography>
                        </Box>
                    ) : activeTabs.length > 0 ? (
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={tabValue} onChange={handleTabChange} aria-label="leave history tabs">
                                    {activeTabs.map((type, index) => (
                                        <Tab label={type} key={type} />
                                    ))}
                                </Tabs>
                            </Box>
                            {activeTabs.map((type, index) => (
                                <div
                                    role="tabpanel"
                                    hidden={tabValue !== index}
                                    id={`tabpanel-${index}`}
                                    aria-labelledby={`tab-${index}`}
                                    key={type}
                                >
                                    {tabValue === index && (
                                        <Box sx={{ p: 2 }}>
                                            <MasterCommonTable
                                                isPaginationBar={false}
                                                columns={historyColumns}
                                                data={formatHistoryForTable(groupedHistory[type])}
                                                openLoader={false}
                                                tableConfig={{ isView: false, isAction: false }}
                                            />
                                        </Box>
                                    )}
                                </div>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                            <img src={EmptyData_Image} alt="No Data" style={{ width: 150, marginBottom: 16 }} />
                            <Typography variant="subtitle1" color="textSecondary">No leave history found for this period.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Earned Credit History Dialog */}
            <Dialog
                open={earnedCreditDialog}
                onClose={() => setEarnedCreditDialog(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Earned Leave Credit History
                    <IconButton
                        aria-label="close"
                        onClick={() => setEarnedCreditDialog(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {earnedCreditLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <Typography>Loading...</Typography>
                        </Box>
                    ) : earnedCreditData.length > 0 ? (
                        <>
                            {/* Summary Statistics */}
                            <Box sx={{ p: 3, borderBottom: '2px solid #e0e0e0' }}>
                                <Grid container spacing={3} justifyContent="center">
                                    <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Chip
                                            label={`Carry Forward: ${earnedCreditData.find(item => item.date === "1st Jan")?.count || 0}`}
                                            sx={{
                                                bgcolor: 'rgba(0, 128, 128, 0.12)',
                                                color: '#008080',
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                height: '42px',
                                                px: 3,
                                                borderRadius: '8px',
                                                border: '1px solid rgba(0, 128, 128, 0.2)',
                                                '& .MuiChip-label': { px: 1, whiteSpace: 'nowrap' },
                                                minWidth: 'fit-content'
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Chip
                                            label={`Total Earned: ${earnedCreditData.filter(item => item.type === "Credit" && item.date !== "1st Jan").reduce((sum, item) => sum + item.count, 0)}`}
                                            sx={{
                                                bgcolor: 'rgba(0, 128, 128, 0.12)',
                                                color: '#008080',
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                height: '42px',
                                                px: 3,
                                                borderRadius: '8px',
                                                border: '1px solid rgba(0, 128, 128, 0.2)',
                                                '& .MuiChip-label': { px: 1, whiteSpace: 'nowrap' },
                                                minWidth: 'fit-content'
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Chip
                                            label={`Total Applied: ${Math.abs(earnedCreditData.filter(item => item.type === "Debit").reduce((sum, item) => sum + item.count, 0))}`}
                                            sx={{
                                                bgcolor: 'rgba(239, 68, 68, 0.12)',
                                                color: '#ef4444',
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                height: '42px',
                                                px: 3,
                                                borderRadius: '8px',
                                                border: '1px solid rgba(239, 68, 68, 0.2)',
                                                '& .MuiChip-label': { px: 1, whiteSpace: 'nowrap' },
                                                minWidth: 'fit-content'
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <Chip
                                            label={`Current Balance: ${earnedCreditData.reduce((sum, item) => sum + item.count, 0)}`}
                                            sx={{
                                                bgcolor: '#008080',
                                                color: '#ffffff',
                                                fontWeight: 600,
                                                fontSize: '0.95rem',
                                                height: '42px',
                                                px: 3,
                                                borderRadius: '8px',
                                                boxShadow: '0 2px 4px rgba(0, 128, 128, 0.2)',
                                                minWidth: 'fit-content',
                                                '& .MuiChip-label': { px: 1, whiteSpace: 'nowrap' }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Table */}
                            <Box sx={{ p: 2 }}>
                                <MasterCommonTable
                                    isPaginationBar={false}
                                    columns={earnedCreditColumns}
                                    data={earnedCreditData}
                                    openLoader={earnedCreditLoading}
                                    tableConfig={{ isView: false, isAction: false }}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
                            <img src={EmptyData_Image} alt="No Data" style={{ width: 150, marginBottom: 16 }} />
                            <Typography variant="subtitle1" color="textSecondary">No earned leave credits found.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box >
    );
};

export default LeaveSummary;
